import React, { useCallback } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  Box,
  IconButton,
  Typography,
  Paper,
} from '@mui/material';
import { IMaskInput } from 'react-imask';
import DeleteIcon from '@mui/icons-material/Delete';
import type { ProfileType } from '../../../../utils/profiles';
import ProfileSelect from '../ProfileSelect';

export interface Contact {
  id?: string;
  fullName: string;
  email: string;
  phone: string;
  profiles: ProfileType[];
  cpf: string;
  jobTitle: string;
}

interface ContactFormProps {
  contact: Contact;
  index: number;
  onUpdate: (index: number, field: keyof Contact, value: any) => void;
  onRemove: (index: number) => void;
  errors?: Record<string, any>;
  onValidationError?: (index: number, field: string, message: string) => void;
  allContacts?: Contact[];
}

const ContactForm: React.FC<ContactFormProps> = ({
  contact,
  index,
  onUpdate,
  onRemove,
  errors,
  onValidationError,
  allContacts,
}) => {
  const handleRemove = useCallback(() => onRemove(index), [onRemove, index]);

  const checkDuplicateField = (field: 'email' | 'cpf', value: string) => {
    if (!value || !allContacts || !onValidationError) return;

    const cleanValue = field === 'cpf' ? value.replace(/[^\d]/g, '') : value.toLowerCase();
    
    const duplicateIndex = allContacts.findIndex((c, i) => {
      const compareValue = field === 'cpf' 
        ? c[field]?.replace(/[^\d]/g, '') 
        : c[field]?.toLowerCase();
      return i !== index && compareValue === cleanValue;
    });

    if (duplicateIndex !== -1) {
      onValidationError(
        index, 
        field, 
        `Este ${field.toUpperCase()} já está sendo usado no contato ${duplicateIndex + 1}`
      );
    }
  };

  const handleFieldChange = (field: keyof Contact) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    onUpdate(index, field, value);
    onValidationError?.(index, field, '');
  };

  const handleFieldBlur = (field: keyof Contact, value: string) => {
    if ((field === 'email' || field === 'cpf') && value) {
      checkDuplicateField(field, value);
    }
  };

  const handleMaskedFieldChange = (field: keyof Contact) => (value: string) => {
    const cleanValue = String(value.replace(/[^\d]/g, ''));
    onUpdate(index, field, cleanValue);
    onValidationError?.(index, field, '');
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">Contato {index + 1}</Typography>
        <IconButton color="error" onClick={handleRemove} size="small">
          <DeleteIcon />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            required
            variant="standard"
            label="Nome Completo"
            value={contact.fullName}
            onChange={handleFieldChange('fullName')}
            error={Boolean(errors?.contacts?.[index]?.fullName)}
            helperText={errors?.contacts?.[index]?.fullName}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            required
            variant="standard"
            label="E-mail"
            value={contact.email}
            onChange={handleFieldChange('email')}
            onBlur={() => handleFieldBlur('email', contact.email)}
            error={Boolean(errors?.contacts?.[index]?.email)}
            helperText={errors?.contacts?.[index]?.email}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth variant="standard">
            <TextField
              fullWidth
              required
              variant="standard"
              label="CPF"
              error={Boolean(errors?.contacts?.[index]?.cpf)}
              helperText={errors?.contacts?.[index]?.cpf}
              InputProps={{
                inputComponent: IMaskInput as any,
                inputProps: {
                  mask: '000.000.000-00',
                  value: contact.cpf,
                  onAccept: handleMaskedFieldChange('cpf'),
                  onBlur: () => handleFieldBlur('cpf', contact.cpf),
                },
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            variant="standard"
            label="Cargo"
            value={contact.jobTitle}
            onChange={handleFieldChange('jobTitle')}
            error={Boolean(errors?.contacts?.[index]?.jobTitle)}
            helperText={errors?.contacts?.[index]?.jobTitle}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth variant="standard">
            <TextField
              fullWidth
              required
              variant="standard"
              label="Telefone"
              error={Boolean(errors?.contacts?.[index]?.phone)}
              helperText={errors?.contacts?.[index]?.phone}
              InputProps={{
                inputComponent: IMaskInput as any,
                inputProps: {
                  mask: '+{55} 00 00000-0000',
                  value: contact.phone,
                  onAccept: handleMaskedFieldChange('phone'),
                },
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <ProfileSelect
            contact={contact}
            index={index}
            onUpdate={onUpdate}
            errors={errors}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ContactForm; 
